// Settings → Ping (admins): push on this device (and the other devices that get it), the Ping
// iOS app's signed-in devices, what pushes and when (levels, quiet hours, the workspace "Also
// email alerts" switch), and the sources that send to Ping, with their tokens and snippets.
import type { Level, NewToken, NotifyDevice, NotifySettingsView, NotifySourcesView, NotifySourceView, NotifyTestResult } from "../src/inbox-types";
import { avatar } from "./inbox";
import { disablePush, enablePush, endpointHash, isIos, isStandalone, pushState, registerWorker, type PushState } from "./push";

interface Opts {
  el: HTMLElement;
  current: () => boolean;
}

/** A Ping iOS app install signed in with a device key (GET /api/auth/devices, dashboard/src/devices.ts). */
interface AppDevice {
  id: string;
  name: string;
  platform: string;
  prefix: string;
  amr: string;
  created_at: number;
  last_used_at: number | null;
  push: boolean;
  current: boolean;
}

/** "Ping" on ping.monoblock.ae, "MonoLog" on log.monoblock.ae: what the Home Screen app is called here. */
const APP = document.documentElement.dataset.app === "ping" ? "Ping" : "MonoLog";

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

const ERRORS: Record<string, string> = {
  bad_name: "Give the source a name (up to 40 characters).",
  bad_icon: "Use a single emoji, or leave it empty for the name's initial.",
  bad_color: "Pick one of the colours.",
  unknown_project: "That project no longer exists.",
  too_many_tokens: "A source can have up to 10 tokens. Revoke one first.",
  bad_timezone: "That time zone isn't recognised.",
  bad_quiet_start: "Enter quiet hours as times, like 22:00.",
  bad_quiet_end: "Enter quiet hours as times, like 07:00.",
  denied: "Notifications are blocked for this site. Allow them in the browser's site settings, then try again.",
  dismissed: "The browser didn't get permission. Try again and choose Allow.",
  unsupported: "This browser can't receive push notifications.",
  bad_endpoint: "This browser's push service isn't one MonoLog sends to.",
  not_allowed: "Only MonoLog admins can use Ping.",
};
const msg = (err: unknown) => ERRORS[(err as Error).message] ?? `Something went wrong (${(err as Error).message}).`;

const LEVELS: { key: Level; label: string; hint: string }[] = [
  { key: "critical", label: "Critical", hint: "Outages, failures" },
  { key: "warning", label: "Warning", hint: "Needs a look" },
  { key: "success", label: "Success", hint: "Good news" },
  { key: "info", label: "Info", hint: "Everything else" },
];
const COLORS = ["#2f5bd3", "#16233d", "#2f9e63", "#e0a43a", "#d2553c", "#8e5bd3", "#1f9aa8", "#c2507e", "#7a8699"];
const EMOJI = ["🛒", "🚀", "💳", "📦", "🖥️", "🔔", "📈", "🤖", "🧾", "📱", "⚙️", "🗄️"];

const fmtDay = (ms: number) => new Date(ms).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
function ago(ms: number | null): string {
  if (!ms) return "never";
  const s = (Date.now() - ms) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86_400) return `${Math.round(s / 3600)} h ago`;
  if (s < 30 * 86_400) return `${Math.round(s / 86_400)} days ago`;
  return fmtDay(ms);
}

// ---------- snippets ----------

type Lang = "curl" | "js" | "swift" | "kotlin" | "python";
const LANGS: [Lang, string][] = [
  ["curl", "curl"],
  ["js", "JavaScript"],
  ["swift", "Swift"],
  ["kotlin", "Kotlin"],
  ["python", "Python"],
];
let lang: Lang = "curl";

export function snippet(l: Lang, endpoint: string, token: string): string {
  switch (l) {
    case "curl":
      return `curl ${endpoint} \\
  -H "Authorization: Bearer ${token}" \\
  -d title="Backup finished" \\
  -d message="**12 GB** in 4 minutes" \\
  -d level=success`;
    case "js":
      return `await fetch("${endpoint}", {
  method: "POST",
  headers: {
    Authorization: "Bearer ${token}",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    title: "New order #1042",
    message: "**AED 1,250** from Dubai",
    level: "success",
    url: "https://example.com/orders/1042",
    url_title: "Open order",
    tags: ["orders"],
    data: { items: 3, payment: "card" },
  }),
});`;
    case "swift":
      return `var request = URLRequest(url: URL(string: "${endpoint}")!)
request.httpMethod = "POST"
request.setValue("Bearer ${token}", forHTTPHeaderField: "Authorization")
request.setValue("application/json", forHTTPHeaderField: "Content-Type")
request.httpBody = try JSONSerialization.data(withJSONObject: [
    "title": "Sync finished",
    "message": "Imported 240 items",
    "level": "info",
])
let (_, response) = try await URLSession.shared.data(for: request)`;
    case "kotlin":
      return `// OkHttp
val body = JSONObject()
    .put("title", "Payment failed")
    .put("message", "Card declined for order #1042")
    .put("level", "critical")
    .toString()
    .toRequestBody("application/json".toMediaType())

val request = Request.Builder()
    .url("${endpoint}")
    .header("Authorization", "Bearer ${token}")
    .post(body)
    .build()

OkHttpClient().newCall(request).execute().use { }`;
    case "python":
      return `import requests

requests.post(
    "${endpoint}",
    headers={"Authorization": "Bearer ${token}"},
    json={
        "title": "Nightly import failed",
        "message": "Exit code 1, see \`import.log\`",
        "level": "critical",
        "dedupe_key": "nightly-import",  # repeats within 10 min count up
    },
    timeout=10,
)`;
  }
}

const KEYWORDS: Record<Lang, string[]> = {
  curl: ["curl"],
  js: ["await", "const", "let", "new", "true", "false"],
  swift: ["var", "let", "try", "await", "import"],
  kotlin: ["val", "var", "fun", "import"],
  python: ["import", "from", "True", "False", "None"],
};

/** Just enough colour to read: strings, comments, keywords, flags and numbers. Escaped first. */
export function highlight(code: string, l: Lang): string {
  const kw = KEYWORDS[l].join("|");
  const re = new RegExp(
    String.raw`("(?:[^"\\\n]|\\.)*"|'(?:[^'\\\n]|\\.)*')|(${l === "python" || l === "curl" ? "#" : "//"}[^\n]*)|\b(${kw})\b|(\s-[A-Za-z]\b|\s--[a-z-]+)|\b(\d+(?:\.\d+)?)\b`,
    "g",
  );
  let out = "";
  let last = 0;
  for (const m of code.matchAll(re)) {
    out += esc(code.slice(last, m.index));
    const cls = m[1] ? "s" : m[2] ? "c" : m[3] ? "k" : m[4] ? "f" : "n";
    out += `<span class="tk-${cls}">${esc(m[0])}</span>`;
    last = m.index + m[0].length;
  }
  return out + esc(code.slice(last));
}

function codeBlock(endpoint: string, token: string, secret: boolean): string {
  return `<div class="nfy-code">
    <div class="nfy-code-head"><div class="nfy-langs" role="tablist">${LANGS.map(([k, label]) => `<button role="tab" data-nfy-lang="${k}" aria-selected="${k === lang}">${label}</button>`).join("")}</div>
      <button class="nfy-copy" data-nfy-copy title="Copy">Copy</button></div>
    <pre data-token="${esc(token)}" data-endpoint="${esc(endpoint)}"><code>${highlight(snippet(lang, endpoint, token), lang)}</code></pre>
    ${secret ? "" : `<p class="nfy-code-note">Replace <code>nt_YOUR_TOKEN</code> with one of this source's tokens. Fields: <code>title</code> (required), <code>message</code>, <code>level</code> (info, success, warning, critical), <code>url</code>, <code>url_title</code>, <code>tags</code>, <code>data</code>, <code>dedupe_key</code>, <code>ttl_days</code>.</p>`}
  </div>`;
}

// ---------- page state ----------

let settings: NotifySettingsView | null = null;
let sources: NotifySourcesView | null = null;
let push: PushState = "unsupported";
let thisDevice: string | null = null; // hash of this browser's endpoint
let openSource: string | null = null;
let editing: string | null = null; // source id or "new"
let newToken: NewToken | null = null;
let apps: AppDevice[] = [];

export async function renderNotifySettings(o: Opts): Promise<void> {
  o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Ping</h2></div><div class="acct-msg">Loading…</div></div>`;
  try {
    [settings, sources, push, thisDevice, apps] = await Promise.all([
      call<NotifySettingsView>("GET", "notify/settings"),
      call<NotifySourcesView>("GET", "notify/sources"),
      pushState(),
      endpointHash().catch(() => null),
      call<{ devices: AppDevice[] }>("GET", "auth/devices")
        .then((r) => r.devices)
        .catch(() => [] as AppDevice[]),
    ]);
  } catch (err) {
    if (o.current()) o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Ping</h2></div><div class="acct-msg error">Couldn't load notification settings (${esc((err as Error).message)}).</div></div>`;
    return;
  }
  if (!o.current()) return;
  draw(o);
}

function deviceRow(d: NotifyDevice): string {
  const mine = d.kind === "webpush" && d.endpoint_hash === thisDevice;
  const status = d.last_error && d.failures ? `<span class="nfy-bad">Failing: ${esc(d.last_error.slice(0, 80))}</span>` : d.last_success_at ? `Last delivered ${ago(d.last_success_at)}` : "Nothing delivered yet";
  const tag = mine ? ` <span class="pill none">This device</span>` : d.kind === "apns" ? ` <span class="pill none">Ping app${d.environment === "sandbox" ? " · sandbox" : ""}</span>` : "";
  return `<li class="nfy-dev"><span class="nfy-dev-ic" aria-hidden="true">${d.kind === "apns" || /iPhone|iPad|Android/.test(d.label) ? "📱" : "💻"}</span>
    <span class="nfy-dev-main"><b>${esc(d.label || d.service)}${tag}</b><span class="muted">${status} · added ${fmtDay(d.created_at)}</span></span>
    <button class="link danger" data-nfy-remove-dev="${esc(d.id)}">Remove</button></li>`;
}

function pushCard(): string {
  const s = settings!;
  const mine = s.devices.find((d) => d.endpoint_hash === thisDevice);
  const on = push === "on" && !!mine;
  let title: string;
  let body: string;
  let action = "";
  if (!s.push.configured) {
    title = "Push isn't set up yet";
    body = "The server has no VAPID key, so browsers get no push (notifications still arrive in Ping). Run <code>node scripts/gen-vapid.mjs</code> and set <code>VAPID_PUBLIC_KEY</code> and the <code>VAPID_PRIVATE_KEY</code> secret on both Workers (README, “Ping”).";
  } else if (push === "needs-install") {
    title = `Add ${APP} to your Home Screen`;
    body = `On iPhone and iPad, push only works from the Home Screen app: tap <b>Share</b>, then <b>Add to Home Screen</b>, open ${APP} from there and turn notifications on. Or use the Ping iOS app.`;
  } else if (push === "unsupported") {
    title = "This browser can't receive push";
    body = isIos() ? `Open ${APP} from the Home Screen (iOS 16.4 or later) to get notifications.` : "Try Chrome, Edge, Firefox or Safari on a desktop or Android.";
  } else if (push === "denied") {
    title = "Notifications are blocked";
    body = `This browser has blocked notifications for ${APP}. Allow them in the site's settings (the icon left of the address), then come back.`;
  } else if (on) {
    title = "Push is on for this device";
    body = `Notifications that match the levels below arrive here even when ${APP} is closed.`;
    action = `<button class="btn" data-nfy-push="off">Turn off</button>`;
  } else {
    title = "Get notifications on this device";
    body = `Your browser will ask for permission.${isIos() && isStandalone() ? "" : ` On iPhone, add ${APP} to the Home Screen first, or use the Ping iOS app.`}`;
    action = `<button class="btn primary" data-nfy-push="on">Turn on push</button>`;
  }
  const ready = (s.push.configured || s.push.apns) && s.devices.length > 0;
  return `<div class="card nfy-card">
    <div class="nfy-push ${on ? "on" : ""}">
      <span class="nfy-bell" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3.5a5.5 5.5 0 0 0-5.5 5.5v3.6L4.8 15.7h14.4l-1.7-3.1V9A5.5 5.5 0 0 0 12 3.5Z"/><path d="M10 18.6a2 2 0 0 0 4 0"/></svg>${on ? "<i></i>" : ""}</span>
      <div class="nfy-push-text"><h2>${title}</h2><p>${body}</p></div>
      <div class="nfy-push-acts">${action}<button class="btn" data-nfy-test ${ready ? "" : "disabled"} title="${ready ? "Sends a notification to your devices now" : "Turn push on first"}">Send test</button></div>
    </div>
    <p class="acct-msg nfy-push-msg" role="status"></p>
    ${s.devices.length ? `<div class="nfy-sub"><h3>Your devices</h3><ul class="nfy-devs">${s.devices.map(deviceRow).join("")}</ul></div>` : ""}
  </div>`;
}

const AMR: Record<string, string> = { session: "from the web", password: "with a password", code: "with an email code" };

/** The Ping iOS app: each signed-in install, with Revoke (signs it out and stops its pushes). */
function appsCard(): string {
  const s = settings!;
  const rows = apps
    .map(
      (d) => `<li class="nfy-dev"><span class="nfy-dev-ic" aria-hidden="true">📱</span>
    <span class="nfy-dev-main"><b>${esc(d.name)}${d.push ? ` <span class="pill none">Push on</span>` : ""}</b><span class="muted">Signed in ${AMR[d.amr] ?? ""} ${fmtDay(d.created_at)} · last seen ${ago(d.last_used_at)} · key <code>${esc(d.prefix)}…</code></span></span>
    <button class="link danger" data-nfy-revoke-app="${esc(d.id)}">Revoke</button></li>`,
    )
    .join("");
  return `<div class="card nfy-card">
    <div class="card-head"><h2>Ping app</h2></div>
    <p class="muted nfy-intro">The iOS app signs in with your email (a code, or your password) and gets its own key. Revoking a key signs that iPhone out and stops its notifications; so does changing your password.${
      s.push.apns ? "" : " APNs isn't set up on the server yet, so the app can't get push notifications (README, “Ping”)."
    }</p>
    ${rows ? `<ul class="nfy-devs">${rows}</ul>` : `<p class="muted nfy-note">No iPhones signed in yet.</p>`}
  </div>`;
}

function deliveryCard(): string {
  const s = settings!.settings;
  const zones = (() => {
    try {
      return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf("timeZone");
    } catch {
      return [s.timezone, "UTC"];
    }
  })();
  if (!zones.includes(s.timezone)) zones.unshift(s.timezone);
  return `<div class="card nfy-card">
    <div class="card-head"><h2>What pushes</h2></div>
    <form class="nfy-form" id="nfy-delivery">
      <fieldset class="nfy-levels"><legend>Push these levels</legend>
        ${LEVELS.map(
          (l) =>
            `<label class="nfy-level lv-${l.key}"><input type="checkbox" name="lvl" value="${l.key}"${s.push_levels.includes(l.key) ? " checked" : ""}><span class="nfy-lv-dot"></span><span><b>${l.label}</b><small>${l.hint}</small></span></label>`,
        ).join("")}
      </fieldset>
      <p class="muted nfy-note">Everything still lands in Ping; this only decides what buzzes your phone.</p>
      <div class="nfy-quiet">
        <label class="nfy-switch"><input type="checkbox" name="quiet_on"${s.quiet_on ? " checked" : ""}><span class="sw" aria-hidden="true"></span><span><b>Quiet hours</b><small>Hold pushes overnight</small></span></label>
        <div class="nfy-quiet-times"${s.quiet_on ? "" : " data-off"}>
          <label class="fld"><span>From</span><input class="acct-input" type="time" name="quiet_start" value="${s.quiet_start}" required></label>
          <label class="fld"><span>To</span><input class="acct-input" type="time" name="quiet_end" value="${s.quiet_end}" required></label>
          <label class="fld nfy-tz"><span>Time zone</span><select class="btn" name="timezone">${zones.map((z) => `<option${z === s.timezone ? " selected" : ""}>${esc(z)}</option>`).join("")}</select></label>
        </div>
        <label class="nfy-check"><input type="checkbox" name="critical_bypass"${s.critical_bypass ? " checked" : ""}><span>Critical notifications still come through during quiet hours</span></label>
      </div>
      <div class="nfy-email">
        <label class="nfy-switch"><input type="checkbox" name="email_alerts"${settings!.email_alerts ? " checked" : ""}><span class="sw" aria-hidden="true"></span><span><b>Also email alerts</b><small>Traffic, uptime, certificate, error spike and failed-event alerts go to Ping either way. Turn this off to stop the emails once push works for you. Applies to everyone who gets MonoLog's alert emails.</small></span></label>
      </div>
      <div class="usr-actions"><button class="btn primary" type="submit">Save</button><span class="acct-msg" role="status"></span></div>
    </form>
  </div>`;
}

function sourceForm(s: NotifySourceView | null): string {
  const color = s?.color ?? COLORS[0]!;
  const projects = sources!.projects;
  return `<form class="nfy-src-form" data-nfy-src-form="${esc(s?.id ?? "new")}">
    <div class="nfy-src-preview" aria-hidden="true">${avatar({ name: s?.name || "N", icon: s?.icon ?? "", color }, "md")}</div>
    <div class="nfy-src-fields">
      <label class="fld"><span>Name</span><input class="acct-input" name="name" maxlength="40" required placeholder="Kanzen shop" value="${esc(s?.name ?? "")}" autocomplete="off"></label>
      <div class="fld"><span>Icon</span><div class="nfy-emoji"><input class="acct-input" name="icon" maxlength="16" placeholder="Aa" value="${esc(s?.icon ?? "")}" aria-label="Emoji (empty for the initial)">${EMOJI.map((e) => `<button type="button" data-nfy-emoji="${e}" aria-label="Use ${e}">${e}</button>`).join("")}</div></div>
      <div class="fld"><span>Colour</span><div class="nfy-colors">${COLORS.map((c) => `<label style="--c:${c}"><input type="radio" name="color" value="${c}"${c === color ? " checked" : ""} aria-label="${c}"><i></i></label>`).join("")}</div></div>
      <label class="fld"><span>Linked project <small class="muted">optional</small></span><select class="btn" name="project_id"><option value="">None</option>${projects.map((p) => `<option value="${esc(p.id)}"${p.id === s?.project_id ? " selected" : ""}>${esc(p.name)}</option>`).join("")}</select></label>
      <div class="usr-actions"><button class="btn primary" type="submit">${s ? "Save" : "Create source"}</button><button class="btn" type="button" data-nfy-cancel>Cancel</button>${
        s ? `<button class="link danger nfy-del" type="button" data-nfy-delete="${esc(s.id)}">Delete source</button>` : ""
      }<span class="acct-msg" role="status"></span></div>
    </div>
  </form>`;
}

function sourceRow(s: NotifySourceView): string {
  const open = openSource === s.id;
  const meta = s.system
    ? "Built in: MonoLog's own traffic, uptime, error and failed-event alerts"
    : [s.project_name ? `Linked to ${esc(s.project_name)}` : "", s.total ? `${s.total.toLocaleString()} in Ping` : "Nothing sent yet", s.last_at ? `last ${ago(s.last_at)}` : ""].filter(Boolean).join(" · ");
  if (editing === s.id) return `<li class="nfy-src editing">${sourceForm(s)}</li>`;
  const fresh = newToken && newToken.source_id === s.id ? newToken : null;
  const body = open && !s.system
    ? `<div class="nfy-src-body">
        ${
          fresh
            ? `<div class="nfy-secret"><div><b>New token</b><span>Copy it now: it isn't shown again.</span></div><div class="nfy-secret-row"><code>${esc(fresh.token)}</code><button class="btn" data-nfy-copy-token="${esc(fresh.token)}">Copy</button></div></div>`
            : ""
        }
        <div class="nfy-tokens">
          <div class="nfy-tokens-head"><h3>Tokens</h3><form class="nfy-token-new" data-nfy-token-form="${esc(s.id)}"><input class="acct-input" name="label" maxlength="60" placeholder="Label, e.g. production" aria-label="Token label"><button class="btn" type="submit">New token</button></form></div>
          ${
            s.tokens.length
              ? `<ul>${s.tokens
                  .map(
                    (t) =>
                      `<li><code class="nfy-prefix">${esc(t.prefix)}…</code><span class="nfy-tok-label">${esc(t.label || "Untitled")}</span><span class="muted">Created ${fmtDay(t.created_at)} · used ${ago(t.last_used_at)}</span><button class="link danger" data-nfy-revoke="${esc(t.id)}">Revoke</button></li>`,
                  )
                  .join("")}</ul>`
              : `<p class="muted nfy-note">No tokens yet. Create one to start sending.</p>`
          }
        </div>
        ${codeBlock(sources!.endpoint, fresh?.token ?? "nt_YOUR_TOKEN", !!fresh)}
      </div>`
    : "";
  return `<li class="nfy-src${open ? " open" : ""}">
    <div class="nfy-src-row"${s.system ? "" : ` role="button" tabindex="0" data-nfy-open="${esc(s.id)}" aria-expanded="${open}"`}>
      ${avatar(s, "md")}
      <span class="nfy-src-main"><b>${esc(s.name)}</b><span class="muted">${meta}</span></span>
      ${s.system ? `<span class="pill none">Built in</span>` : `<span class="nfy-tok-count">${s.tokens.length ? `${s.tokens.length} ${s.tokens.length === 1 ? "token" : "tokens"}` : "No tokens"}</span><button class="link" data-nfy-edit="${esc(s.id)}">Edit</button><span class="nfy-chev" aria-hidden="true"></span>`}
    </div>
    ${body}
  </li>`;
}

function sourcesCard(): string {
  const list = sources!.sources;
  return `<div class="card nfy-card">
    <div class="card-head"><h2>Sources</h2><span class="spacer" style="flex:1"></span>${editing === "new" ? "" : `<button class="btn ink" data-nfy-new><span aria-hidden="true">+</span> New source</button>`}</div>
    <p class="muted nfy-intro">A source is a site, app or script that sends notifications. Each has its own tokens; send with one request to <code>${esc(sources!.endpoint)}</code>.</p>
    ${editing === "new" ? `<div class="nfy-src editing new">${sourceForm(null)}</div>` : ""}
    <ul class="nfy-srcs">${list.map(sourceRow).join("")}</ul>
    ${list.filter((s) => !s.system).length ? "" : `<div class="nfy-first">${codeBlock(sources!.endpoint, "nt_YOUR_TOKEN", false)}</div>`}
  </div>`;
}

function draw(o: Opts): void {
  o.el.innerHTML = `<div class="nfy">${pushCard()}${appsCard()}${deliveryCard()}${sourcesCard()}</div>`;
  wire(o);
}

async function reloadSources(o: Opts): Promise<void> {
  sources = await call<NotifySourcesView>("GET", "notify/sources");
  if (o.current()) draw(o);
}

async function copy(text: string, btn: HTMLElement): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    const was = btn.textContent;
    btn.textContent = "Copied";
    btn.classList.add("done");
    setTimeout(() => {
      btn.textContent = was;
      btn.classList.remove("done");
    }, 1400);
  } catch {
    btn.textContent = "Select and copy";
  }
}

function wire(o: Opts): void {
  const el = o.el;
  const pushMsg = el.querySelector<HTMLElement>(".nfy-push-msg")!;
  el.querySelector<HTMLButtonElement>("[data-nfy-push]")?.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    b.disabled = true;
    pushMsg.className = "acct-msg nfy-push-msg";
    pushMsg.textContent = b.dataset.nfyPush === "on" ? "Asking for permission…" : "Turning off…";
    try {
      if (b.dataset.nfyPush === "on") await enablePush(settings!.push.public_key!);
      else await disablePush(settings!.devices.find((d) => d.endpoint_hash === thisDevice)?.id ?? null);
      await renderNotifySettings(o);
    } catch (err) {
      b.disabled = false;
      pushMsg.className = "acct-msg nfy-push-msg error";
      pushMsg.textContent = msg(err);
    }
  });
  el.querySelector<HTMLButtonElement>("[data-nfy-test]")?.addEventListener("click", async (e) => {
    const b = e.currentTarget as HTMLButtonElement;
    b.disabled = true;
    pushMsg.className = "acct-msg nfy-push-msg";
    pushMsg.textContent = "Sending…";
    try {
      const r = await call<NotifyTestResult>("POST", "notify/test", {});
      pushMsg.textContent = r.sent
        ? `Sent to ${r.sent} ${r.sent === 1 ? "device" : "devices"}. It's in Ping too.${r.failed ? ` ${r.failed} failed.` : ""}`
        : r.removed
          ? "That device's subscription had expired and was removed. Turn push on again."
          : r.failed
            ? "The push service refused it; see the device below."
            : "Added to Ping. No devices to push to yet.";
    } catch (err) {
      pushMsg.className = "acct-msg nfy-push-msg error";
      pushMsg.textContent = msg(err);
    }
    b.disabled = false;
  });
  for (const b of el.querySelectorAll<HTMLButtonElement>("[data-nfy-remove-dev]"))
    b.addEventListener("click", async () => {
      const id = b.dataset.nfyRemoveDev!;
      try {
        if (settings!.devices.find((d) => d.id === id)?.endpoint_hash === thisDevice) await disablePush(id);
        else await call("DELETE", `notify/subscriptions/${id}`);
        await renderNotifySettings(o);
      } catch (err) {
        b.textContent = msg(err);
      }
    });

  for (const b of el.querySelectorAll<HTMLButtonElement>("[data-nfy-revoke-app]"))
    b.addEventListener("click", async () => {
      if (b.dataset.confirm !== "1") {
        b.dataset.confirm = "1";
        b.textContent = "Revoke? It signs that iPhone out";
        return;
      }
      try {
        await call("DELETE", `auth/devices/${b.dataset.nfyRevokeApp}`);
        await renderNotifySettings(o);
      } catch (err) {
        b.textContent = msg(err);
      }
    });

  // What pushes
  const form = el.querySelector<HTMLFormElement>("#nfy-delivery")!;
  const times = form.querySelector<HTMLElement>(".nfy-quiet-times")!;
  form.querySelector<HTMLInputElement>('[name="quiet_on"]')!.addEventListener("change", (e) => {
    if ((e.target as HTMLInputElement).checked) delete times.dataset.off;
    else times.dataset.off = "";
  });
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const f = new FormData(form);
    const status = form.querySelector<HTMLElement>("[role=status]")!;
    status.className = "acct-msg";
    status.textContent = "Saving…";
    try {
      settings = await call<NotifySettingsView>("PUT", "notify/settings", {
        settings: {
          push_levels: f.getAll("lvl"),
          quiet_on: f.get("quiet_on") === "on",
          quiet_start: f.get("quiet_start"),
          quiet_end: f.get("quiet_end"),
          timezone: f.get("timezone"),
          critical_bypass: f.get("critical_bypass") === "on",
        },
        email_alerts: f.get("email_alerts") === "on",
      });
      status.textContent = "Saved.";
    } catch (err) {
      status.className = "acct-msg error";
      status.textContent = msg(err);
    }
  });

  // Sources
  el.querySelector("[data-nfy-new]")?.addEventListener("click", () => {
    editing = "new";
    draw(o);
    el.querySelector<HTMLInputElement>('[data-nfy-src-form="new"] [name="name"]')?.focus();
  });
  for (const b of el.querySelectorAll<HTMLElement>("[data-nfy-edit]"))
    b.addEventListener("click", (e) => {
      e.stopPropagation();
      editing = b.dataset.nfyEdit!;
      draw(o);
    });
  for (const r of el.querySelectorAll<HTMLElement>("[data-nfy-open]")) {
    const go = () => {
      openSource = openSource === r.dataset.nfyOpen ? null : r.dataset.nfyOpen!;
      if (newToken && newToken.source_id !== openSource) newToken = null;
      draw(o);
    };
    r.addEventListener("click", (e) => !(e.target as HTMLElement).closest("button") && go());
    r.addEventListener("keydown", (e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), go()));
  }
  for (const f of el.querySelectorAll<HTMLFormElement>("[data-nfy-src-form]")) {
    const preview = f.querySelector<HTMLElement>(".nfy-src-preview")!;
    const refresh = () => {
      const d = new FormData(f);
      preview.innerHTML = avatar({ name: String(d.get("name") || "N"), icon: String(d.get("icon") ?? ""), color: String(d.get("color") ?? COLORS[0]) }, "md");
    };
    f.addEventListener("input", refresh);
    f.addEventListener("change", refresh);
    for (const b of f.querySelectorAll<HTMLButtonElement>("[data-nfy-emoji]"))
      b.addEventListener("click", () => {
        const input = f.querySelector<HTMLInputElement>('[name="icon"]')!;
        input.value = input.value === b.dataset.nfyEmoji ? "" : b.dataset.nfyEmoji!;
        refresh();
      });
    f.querySelector("[data-nfy-cancel]")!.addEventListener("click", () => {
      editing = null;
      draw(o);
    });
    f.querySelector<HTMLButtonElement>("[data-nfy-delete]")?.addEventListener("click", async (e) => {
      const b = e.currentTarget as HTMLButtonElement;
      if (b.dataset.confirm !== "1") {
        b.dataset.confirm = "1";
        b.textContent = "Delete it, its tokens and its notifications?";
        return;
      }
      try {
        await call("DELETE", `notify/sources/${b.dataset.nfyDelete}`);
        editing = null;
        await reloadSources(o);
      } catch (err) {
        b.textContent = msg(err);
      }
    });
    f.addEventListener("submit", async (e) => {
      e.preventDefault();
      const d = new FormData(f);
      const body = { name: String(d.get("name") ?? ""), icon: String(d.get("icon") ?? "").trim(), color: String(d.get("color") ?? COLORS[0]), project_id: String(d.get("project_id") ?? "") || null };
      const status = f.querySelector<HTMLElement>("[role=status]")!;
      try {
        const id = f.dataset.nfySrcForm!;
        if (id === "new") {
          const r = await call<{ id: string }>("POST", "notify/sources", body);
          // A new source gets its first token straight away, so the snippet is ready to paste.
          newToken = await call<NewToken>("POST", `notify/sources/${r.id}/tokens`, { label: "default" });
          openSource = r.id;
        } else await call("PATCH", `notify/sources/${id}`, body);
        editing = null;
        await reloadSources(o);
      } catch (err) {
        status.className = "acct-msg error";
        status.textContent = msg(err);
      }
    });
  }
  for (const f of el.querySelectorAll<HTMLFormElement>("[data-nfy-token-form]"))
    f.addEventListener("submit", async (e) => {
      e.preventDefault();
      try {
        newToken = await call<NewToken>("POST", `notify/sources/${f.dataset.nfyTokenForm}/tokens`, { label: String(new FormData(f).get("label") ?? "") });
        await reloadSources(o);
      } catch (err) {
        f.querySelector<HTMLButtonElement>("button")!.textContent = msg(err);
      }
    });
  for (const b of el.querySelectorAll<HTMLButtonElement>("[data-nfy-revoke]"))
    b.addEventListener("click", async () => {
      if (b.dataset.confirm !== "1") {
        b.dataset.confirm = "1";
        b.textContent = "Revoke? Senders using it stop working";
        return;
      }
      try {
        await call("DELETE", `notify/tokens/${b.dataset.nfyRevoke}`);
        if (newToken?.id === b.dataset.nfyRevoke) newToken = null;
        await reloadSources(o);
      } catch (err) {
        b.textContent = msg(err);
      }
    });
  for (const b of el.querySelectorAll<HTMLButtonElement>("[data-nfy-copy-token]")) b.addEventListener("click", () => void copy(b.dataset.nfyCopyToken!, b));
  for (const b of el.querySelectorAll<HTMLButtonElement>("[data-nfy-lang]"))
    b.addEventListener("click", () => {
      lang = b.dataset.nfyLang as Lang;
      for (const box of el.querySelectorAll<HTMLElement>(".nfy-code")) {
        const pre = box.querySelector<HTMLElement>("pre")!;
        pre.querySelector("code")!.innerHTML = highlight(snippet(lang, pre.dataset.endpoint!, pre.dataset.token!), lang);
        for (const t of box.querySelectorAll<HTMLElement>("[data-nfy-lang]")) t.setAttribute("aria-selected", String(t.dataset.nfyLang === lang));
      }
    });
  for (const b of el.querySelectorAll<HTMLButtonElement>("[data-nfy-copy]"))
    b.addEventListener("click", () => {
      const pre = b.closest(".nfy-code")!.querySelector<HTMLElement>("pre")!;
      void copy(snippet(lang, pre.dataset.endpoint!, pre.dataset.token!), b);
    });
}

/** Registers the service worker early for admins, so "Turn on push" is quick and installs work. */
export const warmPush = () => void registerWorker();
